import { Component, OnInit } from '@angular/core';
import { EVENT } from 'src/app/shared/constants/dali.constants';
import { Changes, TagEvent } from 'src/app/shared/models/dali-tags.model';
import { LoaderService } from 'src/app/shared/services/loader/loader.service';

import { LottieImage } from '../../../shared/models/abre-cuentas.model';
import { ExternalResponse } from '../../../shared/models/channel-communicator/external-response.model';
import { AbreCuentasService } from '../../../shared/services/abre-cuentas/abre-cuentas.service';
import {
  ChannelCommunicatorService,
} from '../../../shared/services/communicator/channel-communicator/channel-communicator.service';
import { CopyService } from '../../../shared/services/copies/copy.service';
import { HeaderService } from '../../../shared/services/header/header.service';
import { IdentificationService } from '../../../shared/services/identification/identification.service';
import { NavigationService } from '../../../shared/services/navigation/navigation-onboarding.service';
import { TaggingService } from '../../../shared/services/tagging/tagging.service';
import { FrontDocumentScanImage } from './front-document-scan-image';

/**
 * Configuración predeterminada del componente
 *
 * *Default component settings*
 */
@Component({
  selector: 'app-front-document-scan',
  templateUrl: './front-document-scan.component.html',
  styleUrls: ['./front-document-scan.component.scss'],
})

/**
 * Clase del componente que escanea la parte frontal del documento
 *
 * *Fron document scan component class*
 *
 * @implements {OnInit}
 */
export class FrontDocumentScanComponent implements OnInit {
  /**
   * Declaración de la variable maxScanAttempts como number, donde se registra los intentos máximos para escanear
   *
   * *Set maxScanAttempts variable as number, it's saved the max scan attempts*
   *
   * @access              public
   * @type                {number}
   */
  maxScanAttempts: number;

  /**
   * Declaración de la variable scanResponse como ExternalResponse, respuesta del servicio del escaneo de la imagen
   *
   * *Set scanResponse variable as ExternalResponse, Service image scanning response*
   *
   * @access              public
   * @type                {ExternalResponse}
   */
  scanResponse: ExternalResponse;

  /**
   * Declaración de la variable image como LottieImage, registro de la imagen del documento
   *
   * *Set image variable as LottieImage, register of the document image*
   *
   * @access              public
   * @type                {LottieImage}
   */
  image: LottieImage;

  /**
   * Summary      Constructor of the component.
   *
   * @param {CopyService}                  copyService                Injectable copyService service.
   * @param {AbreCuentasService}           abreCuentasService         Injectable abreCuentasService service.
   * @param {NavigationService}            navigationService          Injectable navigationService service.
   * @param {ChannelCommunicatorService}   channelCommunicatorService Injectable channelCommunicatorService service.
   * @param {IdentificationService}        identificationService      Injectable identificationService service.
   * @param {HeaderService}                headerService              Injectable headerService service.
   * @var   {number}                       maxScanAttempts            Set max scan attempts.
   * @function headerService.show(true,true) Shows header
   * @function setFirstname Sets default first name
   * @function setHeaderSubscribe
   */
  constructor(
    public copyService: CopyService,
    private abreCuentasService: AbreCuentasService,
    private navigationService: NavigationService,
    private channelCommunicatorService: ChannelCommunicatorService,
    private identificationService: IdentificationService,
    private headerService: HeaderService,
    private taggingService: TaggingService,
    private loaderService: LoaderService,
  ) {
    this.headerService.show(true, true, true);
    this.maxScanAttempts = this.abreCuentasService.getBytteConfig().failedAttempts.frontDocumentScan;
  }

  /**
   * Define la configuración de la imagen una vez el componente es cargada
   *
   * *Sets image configuration once the components is loaded*
   */
  ngOnInit() {
    this.image = {
      renderer: 'svg',
      loop: true,
      autoplay: true,
      animationData: FrontDocumentScanImage,
    };
  }

  /**
   * Función para la captura de la foto
   *
   * *Capture image function*
   */
  async next() {
    // this.loaderService.show();
    try {
      await this.callScanNewVersions();
      // this.loaderService.hide();
    } catch (err) {
      console.error(err);
    }
  }

  /**
   * Función para llamar nuevas versiones del escaneo y sus configuraciones
   *
   * *Function to call new scan version and its configurations*
   */
  async callScanNewVersions() {
    await this.channelCommunicatorService
      .getImpl()
      .verifyAndRequestPermissions()
      .then(p => {
        if (!p.code || p.code !== '200') {
          this.navigationService.navigateToGeneralInformation('401');
          return;
        }

        const channel = this.channelCommunicatorService.getChannelId();
        const androidLicense = this.abreCuentasService.getBytteConfig().licenses[channel].android;
        const iosLicense = this.abreCuentasService.getBytteConfig().licenses[channel].ios;
        const seconds = this.abreCuentasService.getBytteConfig().timeouts.frontDocumentScan.seconds;
        return this.channelCommunicatorService.getImpl().frontDocumentScan(androidLicense, iosLicense, this.abreCuentasService.getTransactionId(), seconds);
      }).catch(() => this.navigationService.navigateToGeneralInformation('500'))
      .then(res => {
        this.scanResponse = res;
        this.resumeApp();
      }).catch(() => this.navigationService.navigateToGeneralInformation('500'));
  }

  /**
   * Casos de respuesta del servicio del escaneo de imagenes
   *
   * *Response cases of the scanning images service*
   */
  resumeApp() {
    switch (this.scanResponse.code) {
      case '400': // Wrong license activation key
      case '403': // Origin not authorized
      case '404': // Operation not found
      case '500': // Internal error
        // this.loaderService.hide();
        this.navigationService.navigateToGeneralInformation('500');
        return;
      case '504': // Failed attempt
        this.abreCuentasService.getAbreCuentasModel().frontDocumentScanAttempts++;
        if (this.abreCuentasService.getAbreCuentasModel().frontDocumentScanAttempts >= this.maxScanAttempts) {
          this.navigationService.pageLoadTag('No fue posible validar identidad', ['pv223', 'pv119', 'pv79', '']);
          this.abreCuentasService.caseInfoError('102');
          this.navigationService.nextStep('102');
        } else {
          this.navigationService.pageLoadTag('Error captura frontal', ['pv221', 'pv112', 'pv72', '']);
          this.abreCuentasService.setImageLottieError(this.image);
          this.navigationService.nextStep('112');
        }
        // this.loaderService.hide();
        return;
    }
    this.navigationService.pageLoadTag(false, ['pv13', 'pv104', 'pv63', 'pv13']);
    const tagPageName = this.taggingService.getTagIdByProduct();
    this.pageTagEvent(tagPageName);
    const document = this.abreCuentasService.proccessBase64(this.scanResponse.data.front_document_photo);
    const face = this.abreCuentasService.proccessBase64(this.scanResponse.data.face_photo);

    this.identificationService
      .sendFrontDocumentInfo(document, face)
      .then(() => {
        this.navigationService.nextStep('200');
      }).catch(() => {
        // this.loaderService.hide();
        this.navigationService.navigateToGeneralInformation('500');
      });
  }

  /**
   * Servicio para enviar datos a Google analytics
   *
   * *Send data to Google Analytics*
   *
   * @param {string} tagPageName
   */
  pageTagEvent(tagPageName: string) {
    try {
      const changes: Changes[] = [
        {
          path: EVENT[`${tagPageName}E1setData`][`${tagPageName}E1transactionName`].path,
          value: EVENT[`${tagPageName}E1setData`][`${tagPageName}E1transactionName`].value,
        },
        {
          path: EVENT[`${tagPageName}E1setData`][`${tagPageName}E1transactionDetailsProductcategory`].path,
          value: EVENT[`${tagPageName}E1setData`][`${tagPageName}E1transactionDetailsProductcategory`].value,
        },
      ];
      const event: TagEvent = {
        category: {
          module: EVENT.customCategory.module.body,
          type: EVENT.customCategory.type.image,
          position: EVENT.customCategory.position.center,
        },
        action: EVENT.customAction.show,
        label: EVENT[`${tagPageName}E1customLabel`],
      };
      this.taggingService.pageTagEvent(event, changes);
    } catch (error) {
      console.log(error);
    }

  }
}
